import React from 'react';
import bemCn from 'bem-cn';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { getInitState } from '../../helpers/spot-slug';
import { RouteFormatter } from '../../routes';
import { showPriceCredits } from '../../helpers/price';
import RequestMorePhotos from '../request-more-photos';
import SpotAmenitiesIcon from '../spot-amenities-icon';
import SpotTopBadge from '../spot-top-badge';
import CarouselSSR from '../carousel/carousel-ssr';
import SvgCommentQuote from 'src/assets/svg/SvgCommentQuote.svg';
import SvgSubscription from 'src/assets/svg/SvgSubscription.svg';
import SvgStarSolid from 'src/assets/svg/SvgStarSolid.svg';
import SvgFenced from 'src/assets/svg/SvgFenced.svg';
import SvgRuler from 'src/assets/svg/SvgRuler.svg';
import SvgPin from 'src/assets/svg/SvgPin.svg';
import './nearby-spots.scss';

const emptyCarouselStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '10px',
    overflow: 'hidden',
};

const SIZE_UNITS = {
    SQ_FT: 'sq ft',
    ACRES: 'acres',
};

const SPOT_ENCLOSURE = {
    FULLY_FENCED: 'Fully Fenced',
    PARTIALLY_FENCED: 'Partially Fenced',
    UNFENCED: 'Unfenced',
};

const showSize = (size, sizeUnits) => {
    const spotSize = parseFloat(size);
    if (SIZE_UNITS[sizeUnits] === SIZE_UNITS.ACRES) {
        if (spotSize < 1) {
            return `${spotSize} acres`;
        }
        if (spotSize == 1) {
            return '1 acre';
        }
        if (spotSize > 1 && spotSize < 10) {
            return `${Math.round(spotSize * 10) / 10} acres`;
        }
        if (spotSize >= 10) {
            return `${parseInt(spotSize)} acres`;
        }
    } else {
        return `${spotSize} ${SIZE_UNITS[sizeUnits] ? SIZE_UNITS[sizeUnits] : 'acres'}`;
    }
};

const showPrice = (price) => {
    const isFromForeign = false; //  myCountry !== 1 || foreign;
    return `${parseFloat(price).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
    })}${isFromForeign ? ' USD' : ''}`;
};

const b = bemCn('nearby-spots-component');

const NearbySpots = ({ items = [], target = '_self', nearbySpotSPhotos }) => {
    const isMobile = useMobileScreen();

    if (!items.length) {
        return '';
    }

    const goTo = (item) => {
        window.open(RouteFormatter.listings({ first: item.spotUrl || item.dogParkUrl }), target);
    };

    return (
        <div className={b()}>
            {items.map((item, index) => {
                const publicPark = Boolean(item.parkName);
                const fixedRating = Math.round(item.rating * 10) / 10;
                const titleTag = publicPark ? 'Public dog park' : 'Private dog park';
                const title = item.title || item.parkName;
                const desc = item.description || item.summary;
                const photos = nearbySpotSPhotos?.find((s) => s.id == item.id)?.spotPhotos || item.allPhotos;
                const comment = item.topReview?.comment
                    ? `"${item.topReview.comment.slice(0, 75)}${item.topReview.comment.length > 75 ? '...' : ''}" `
                    : '';

                const combinedSpot = { ...item, spotPhotos: photos, allPhotos: undefined };

                return (
                    <div key={index} className={b('item')} onClick={() => goTo(item)}>
                        <div className={b('item_carousel')}>
                            {photos?.length > 0 ? (
                                <div className={b('item_carousel_spot-photo-container').mix('carousel')}>
                                    <CarouselSSR {...{ item: combinedSpot, lazy: 'lazy', spotGallery: true, publicPark, showArrow: !isMobile }} />
                                </div>
                            ) : (
                                <div className={b('item_carousel_spot-photo-container')}>
                                    <div style={emptyCarouselStyle}>
                                        <RequestMorePhotos id={item.id} {...{ publicPark }} />
                                    </div>
                                </div>
                            )}
                            <SpotTopBadge spot={item} />
                        </div>
                        <div className={b('tag')}>
                            <span className={b('tag_title', { publicPark })}>{titleTag}</span>
                            {!!item.rating && (
                                <div className={b('tag_rating')}>
                                    <span className={b('tag_rating_icon')}>
                                        <SvgStarSolid />
                                    </span>
                                    <span className="snif-s2 snif-s1-pc snif-medium ms-05 me-05">{fixedRating}</span>
                                    <span className="snif-s2 snif-s1-pc text-medium">({item.reviewsCount})</span>
                                </div>
                            )}
                        </div>
                        <p className={b('title')}>
                            <a href={RouteFormatter.listings({ first: item.spotUrl || item.dogParkUrl })}>{title}</a>
                            {!item.publicPark && item.offerSniffpass && (
                                <span className={b('title_subs')}>
                                    <SvgSubscription />
                                </span>
                            )}
                        </p>
                        {(Boolean(item.enclosureType) || Boolean(item.size)) && (
                            <div className={b('enclosure')}>
                                {Boolean(item.enclosureType) && (
                                    <div className="d-flex align-items-center">
                                        <SvgFenced />
                                        <span className="snif-s1 snif-regular">{SPOT_ENCLOSURE[item.enclosureType.toUpperCase()]}</span>
                                    </div>
                                )}
                                {Boolean(item.enclosureType) && Boolean(item.size) && <div className={b('enclosure_dot')} />}
                                {Boolean(item.size) && (
                                    <div className="d-flex align-items-center">
                                        <SvgRuler />
                                        <span className="snif-s1 snif-regular">{showSize(item.size, item.sizeUnits)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        {desc && <p className={b('desc').mix('snif-s1 text-deep')}>{desc}</p>}
                        {item.allAmenities?.length > 0 && (
                            <div className={b('amenities')}>
                                {item.allAmenities.slice(0, 4).map((amenity) => (
                                    <SpotAmenitiesIcon key={amenity.component} amenities={amenity} />
                                ))}
                            </div>
                        )}
                        {item.topReview?.id && (
                            <div className={b('review')}>
                                <div className={b('review_svg')}>
                                    <SvgCommentQuote />
                                </div>
                                <p>
                                    {comment}
                                    <span>more</span>
                                </p>
                            </div>
                        )}
                        <div className={b('location')}>
                            <p className={b('location_price')}>
                                {publicPark ? (
                                    <>
                                        {item.price ? (
                                            <span className="snif-p snif-semibold">{showPrice(item.price)}</span>
                                        ) : (
                                            <span className="snif-s1 text-deep">No fee info</span>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <span className="snif-p snif-semibold">{showPriceCredits(item.price)} </span>
                                        <span className="snif-s1 text-deep">dog / hour</span>
                                    </>
                                )}
                            </p>
                            <p className={b('location_addr')}>
                                <span className={b('location_addr_icon')}>
                                    <SvgPin />
                                </span>
                                <span>{`${item.city}${item.state ? `, ${getInitState(item.state)}` : ''}`}</span>
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default NearbySpots;
